﻿import * as turf from '@turf/turf';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import { Map as MaplibreMap, MapGeoJSONFeature } from 'maplibre-gl';

// Represents a runway polygon that has been merged (if tile-split) and fitted to its minimum oriented bounding rectangle.
export interface RunwayOBB {
    corners: [[number, number], [number, number], [number, number], [number, number]];
    center: [number, number];
    // Midpoint of the c0–c3 edge (end of the runway).
    endA: [number, number];
    // Midpoint of the c1–c2 edge (other end of the runway).
    endB: [number, number];
    bearingDeg: number;
    properties: Record<string, unknown>;
    featureId: string | number | undefined;
}

// There are 2 issues with runways that this function solves:
// 1. Runway polygons can be split into multiple features if they cross tile boundaries, so we need to merge them back together.
// 2. If we zoom in, we can get polygon that is clipped at the tile edge and has a wrong angle, we need to force it back into rectangle for stripes to render correctly.
function minimumOrientedBoundingRect(points: [number, number][]): [[number, number], [number, number], [number, number], [number, number]] {
    if (points.length < 3) {
        const p = points[0] ?? ([0, 0] as [number, number]);
        return [p, p, p, p];
    }

    // Project to locally-flat space: scale lng by cos(lat) so 1 unit ≈ equal distance in both axes
    const lat0 = points.reduce((s, p) => s + p[1], 0) / points.length;
    const cosLat = Math.cos((lat0 * Math.PI) / 180);
    const toFlat = ([lng, lat]: [number, number]): [number, number] => [lng * cosLat, lat];
    const toLngLat = ([x, y]: [number, number]): [number, number] => [x / cosLat, y];

    const flat = points.map(toFlat);

    const hull = turf.convex(turf.featureCollection(flat.map((p) => turf.point(p))))?.geometry.coordinates[0] as
        | [number, number][]
        | undefined;

    if (!hull || hull.length < 3) {
        const p = points[0];
        return [p, p, p, p];
    }

    const n = hull.length;
    let bestArea = Infinity;
    let bestCorners: [[number, number], [number, number], [number, number], [number, number]] = [hull[0], hull[1], hull[2], hull[0]];

    // Rotating calipers — test each edge of the hull as a candidate rectangle axis
    for (let i = 0; i < n; i++) {
        const a = hull[i];
        const b = hull[(i + 1) % n];

        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const len = Math.hypot(dx, dy);
        if (len === 0) {
            continue;
        }
        const ux = dx / len;
        const uy = dy / len;

        let minU = Infinity,
            maxU = -Infinity,
            minV = Infinity,
            maxV = -Infinity;
        for (const p of hull) {
            const u = p[0] * ux + p[1] * uy;
            const v = -p[0] * uy + p[1] * ux;
            if (u < minU) {
                minU = u;
            }
            if (u > maxU) {
                maxU = u;
            }
            if (v < minV) {
                minV = v;
            }
            if (v > maxV) {
                maxV = v;
            }
        }

        const area = (maxU - minU) * (maxV - minV);
        if (area < bestArea) {
            bestArea = area;
            bestCorners = [
                [minU * ux - minV * uy, minU * uy + minV * ux],
                [maxU * ux - minV * uy, maxU * uy + minV * ux],
                [maxU * ux - maxV * uy, maxU * uy + maxV * ux],
                [minU * ux - maxV * uy, minU * uy + maxV * ux]
            ];
        }
    }

    return bestCorners.map(toLngLat) as [[number, number], [number, number], [number, number], [number, number]];
}

function midpoint(a: [number, number], b: [number, number]): [number, number] {
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function orientCorners(corners: [[number, number], [number, number], [number, number], [number, number]]) {
    const [c0, c1, c2, c3] = corners;

    const d01 = turf.distance(turf.point(c0), turf.point(c1), { units: 'meters' });
    const d12 = turf.distance(turf.point(c1), turf.point(c2), { units: 'meters' });

    if (d01 >= d12) {
        const endA = midpoint(c0, c3);
        const endB = midpoint(c1, c2);
        return { corners: [c0, c1, c2, c3] as typeof corners, endA, endB };
    } else {
        const reordered: typeof corners = [c1, c2, c3, c0];
        const endA = midpoint(c1, c0);
        const endB = midpoint(c2, c3);
        return { corners: reordered, endA, endB };
    }
}

function computeBearing(a: [number, number], b: [number, number]): number {
    const bearing = turf.bearing(turf.point(a), turf.point(b));
    return ((bearing % 360) + 360) % 360;
}

// Tiles may ship without feature ids, so fall back to the runway's identifying properties.
function runwayKey(feature: MapGeoJSONFeature): string | number | undefined {
    if (feature.id && !isNaN(feature.id as number)) {
        return feature.id;
    }
    const p = feature.properties ?? {};
    const airport = p.icaoid ?? p.iataid ?? p.faaid;
    if (airport == null || (p.baseid == null && p.recipid == null)) {
        return undefined;
    }
    return `${airport}|${p.baseid ?? ''}|${p.recipid ?? ''}`;
}

export function computeRunwayOBBs(map: MaplibreMap): RunwayOBB[] {
    const allRunways = map.queryRenderedFeatures({ layers: ['runway'] });

    // Group features by id (tile-split fragments share the same id)
    const runwayMap = new Map<string | number, MapGeoJSONFeature[]>();
    for (const feature of allRunways) {
        const key = runwayKey(feature);
        if (key == null) {
            continue;
        }
        if (!runwayMap.has(key)) {
            runwayMap.set(key, []);
        }
        runwayMap.get(key)!.push(feature);
    }

    const results: RunwayOBB[] = [];

    for (const [featureId, features] of runwayMap) {
        const polygons = features
            .filter((f) => f.geometry.type === 'Polygon')
            .map((f) => turf.polygon((f.geometry as Polygon).coordinates as Position[][]));

        if (polygons.length === 0) {
            continue;
        }

        // Merge tile-split fragments
        const united = polygons.reduce<Feature<Polygon | MultiPolygon>>(
            (acc, poly) => turf.union(turf.featureCollection([acc, poly])) ?? acc,
            polygons[0]
        );

        // Collect all points for OBB
        const allPoints: [number, number][] = [];
        const geom = united.geometry;
        if (geom.type === 'Polygon') {
            for (const ring of geom.coordinates) {
                for (const p of ring) {
                    allPoints.push(p as [number, number]);
                }
            }
        } else if (geom.type === 'MultiPolygon') {
            for (const poly of geom.coordinates) {
                for (const ring of poly) {
                    for (const p of ring) {
                        allPoints.push(p as [number, number]);
                    }
                }
            }
        }

        const rawCorners = minimumOrientedBoundingRect(allPoints);
        const { corners, endA, endB } = orientCorners(rawCorners);
        const center = midpoint(endA, endB);
        const bearingDeg = computeBearing(endA, endB);

        results.push({
            corners,
            center,
            endA,
            endB,
            bearingDeg,
            properties: features[0].properties ?? {},
            featureId
        });
    }

    return results;
}

export function matchesAirport(obb: RunwayOBB, airport: { icaoId: string | null; iataId: string | null; faaId: string | null }): boolean {
    return (
        (!!airport.icaoId && obb.properties.icaoid === airport.icaoId) ||
        (!!airport.iataId && obb.properties.iataid === airport.iataId) ||
        (!!airport.faaId && obb.properties.faaid === airport.faaId)
    );
}

export function getRunwayIds(obb: RunwayOBB): string[] {
    return [obb.properties.baseid as string, obb.properties.recipid as string].filter(Boolean);
}
